import type { CardCapMode, CardMetaGridMinWidth } from "@/types/card";
import type { ComponentSizeType } from "@/types/componentType";
import type { CustomButtonFontWeight } from "@/types/componentType";

/* ── Font weights ───────────────────────────────────────────────────────────── */

/** Weight token → Tailwind class. Static so the scanner emits every class.
    Three rungs only: the webfonts ship 400/600/800 and nothing between. */
export const FONT_WEIGHT_MAP: Record<CustomButtonFontWeight, string> = {
  normal: "font-normal",
  semibold: "font-semibold",
  bold: "font-bold",
};

/* ── Fluid-font ladders — adjacent windows on the project scale ─────────────── */

/** Finest metadata text (breadcrumb selected-value, StyledLabelText label). */
export const MICRO_FONT: Record<ComponentSizeType, string> = {
  small: "text-tiny",
  default: "text-xxxs",
  large: "text-xxs",
};

/** Captions / legends (stepper step label, IconDescriptions text, segment name). */
export const CAPTION_FONT: Record<ComponentSizeType, string> = {
  small: "text-xxxs",
  default: "text-xxs",
  large: "text-xs",
};

/** Control-adjacent labels (switch label, StyledLabelText). */
export const LABEL_FONT: Record<ComponentSizeType, string> = {
  small: "text-xxs",
  default: "text-xs",
  large: "text-s",
};

/** Body text — the workhorse ladder (cards, file-uploader caption, timers). */
export const DISPLAY_TEXT_FONT: Record<ComponentSizeType, string> = {
  small: "text-xs",
  default: "text-s",
  large: "text-m",
};

/** Emphasized text / inline icons (statistic value, typewriter, card-info icon, segment icon). */
export const EMPHASIS_FONT: Record<ComponentSizeType, string> = {
  small: "text-s",
  default: "text-m",
  large: "text-l",
};

/** Standalone icon glyphs (IconTooltip, CustomRotatingIcon). */
export const ICON_FONT: Record<ComponentSizeType, string> = {
  small: "text-m",
  default: "text-l",
  large: "text-xl",
};

/** Hero / dropzone icons (file uploader hero icon). */
export const HERO_ICON_FONT: Record<ComponentSizeType, string> = {
  small: "text-l",
  default: "text-xl",
  large: "text-xxl",
};

/* ── Gap ladders ────────────────────────────────────────────────────────────── */

export const GAP_TIGHT: Record<ComponentSizeType, string> = {
  small: "gap-1",
  default: "gap-1.5",
  large: "gap-2",
};

export const GAP_COZY: Record<ComponentSizeType, string> = {
  small: "gap-1.5",
  default: "gap-2",
  large: "gap-3",
};

export const GAP_TIGHT_X: Record<ComponentSizeType, string> = {
  small: "gap-x-1",
  default: "gap-x-1.5",
  large: "gap-x-2",
};

export const GAP_COZY_X: Record<ComponentSizeType, string> = {
  small: "gap-x-1.5",
  default: "gap-x-2",
  large: "gap-x-3",
};

/* ── Composite ladders (font + padding pairs) ─────────────────────────────────── */

/** Badge / tag font + horizontal-vertical padding. */
export const BADGE_SIZE: Record<ComponentSizeType, { font: string; pad: string }> = {
  small: { font: "text-xxs", pad: "px-1.5 py-0.5" },
  default: { font: "text-xs", pad: "px-2 py-0.5" },
  large: { font: "text-s", pad: "px-3 py-1" },
};

/** Structure-tree row geometry — min row height, row text, and per-level child indent. */
export const TREE_ROW_SIZE: Record<
  ComponentSizeType,
  { rowMinHeight: string; text: string; indent: string }
> = {
  small: { rowMinHeight: "min-h-7", text: "text-xxs", indent: "ps-3 s:ps-4" },
  default: { rowMinHeight: "min-h-9", text: "text-xs", indent: "ps-3 s:ps-5" },
  large: { rowMinHeight: "min-h-11", text: "text-s", indent: "ps-4 s:ps-6" },
};

/** Padded content box (StyledLabelText-style). */
export const BOX_PAD: Record<ComponentSizeType, string> = {
  small: "px-2.5 py-1.5",
  default: "px-3 py-2",
  large: "px-3.5 py-2.5",
};

/* ── Grid ladders ───────────────────────────────────────────────────────────── */

/** CardMetaGrid track floor. Written out literally — Tailwind's scanner cannot
    see a class assembled by interpolation. */
export const META_GRID_MIN: Record<CardMetaGridMinWidth, string> = {
  "140": "grid-cols-[repeat(auto-fill,minmax(140px,1fr))]",
  "160": "grid-cols-[repeat(auto-fill,minmax(160px,1fr))]",
  "180": "grid-cols-[repeat(auto-fill,minmax(180px,1fr))]",
  "200": "grid-cols-[repeat(auto-fill,minmax(200px,1fr))]",
  "210": "grid-cols-[repeat(auto-fill,minmax(210px,1fr))]",
};

/* ── Record-card caps ─────────────────────────────────────────────────────────── */

/** How many info fields a CardRecord shows per viewport class; the overflow spills
    into ModalRecordDetails. Desktop ≥1200px · mobile ≤767px · 768–1199px is tablet. */
export const CARD_INFO_CAPS: Record<CardCapMode, number> = {
  desktop: 4,
  tablet: 3,
  mobile: 2,
};

/** Actions shown on a CardRecord face — a fixed cap at every breakpoint; any beyond
    this move into the details modal. */
export const CARD_ACTION_CAP = 3;
